import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const go = (role) => navigate(next || (["admin", "team"].includes(role) ? "/admin" : "/account"));

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await login(form.email, form.password);
      go(data.role);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      go(data.role);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-sm border border-white/10 bg-[#0A0A0A] p-8">
        <div className="mb-8"><Logo /></div>
        {error && <p data-testid="login-error" className="text-sm text-red-400 mb-4">{error}</p>}
        <Tabs defaultValue="login">
          <TabsList className="bg-white/5 border border-white/10 w-full mb-6">
            <TabsTrigger data-testid="tab-login" value="login" className="flex-1">Sign In</TabsTrigger>
            <TabsTrigger data-testid="tab-register" value="register" className="flex-1">Create Account</TabsTrigger>
          </TabsList>
          <TabsContent value="login">
            <form onSubmit={submitLogin} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Password</Label>
                <Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <button data-testid="login-submit-btn" type="submit" disabled={loading}
                className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white text-sm font-semibold tracking-widest uppercase py-3.5 transition-colors">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </TabsContent>
          <TabsContent value="register">
            <form onSubmit={submitRegister} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Name</Label>
                <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Password (min 8 chars)</Label>
                <Input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <button data-testid="register-submit-btn" type="submit" disabled={loading}
                className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white text-sm font-semibold tracking-widest uppercase py-3.5 transition-colors">
                {loading ? "Creating…" : "Create Account"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
